/**
 * Top toolbar above the Tasks table/board.
 *
 * Contains:
 *   - Saved view chips (All / Assigned to me / Due this week / Blocked / Overdue)
 *     — counts are computed client-side from the fetched task set.
 *   - Table/Board view toggle.
 *   - "New task" button.
 *
 * Filter panels (Phase/Status/Assignee/Sort) are hinted in the design but
 * out of scope for v1 — the saved-view chips cover the primary filtering.
 */
import { ClipboardList, LayoutGrid, Plus, Rows3 } from "lucide-react";
import { Button } from "@/components/ui/button";

export type SavedViewId = "all" | "mine" | "week" | "blocked" | "overdue";
export type ViewMode = "table" | "board";

export interface SavedViewCounts {
  all: number;
  mine: number;
  week: number;
  blocked: number;
  overdue: number;
}

const VIEW_DEFS: Array<{ id: SavedViewId; label: string }> = [
  { id: "all", label: "All tasks" },
  { id: "mine", label: "Assigned to me" },
  { id: "week", label: "Due this week" },
  { id: "blocked", label: "Blocked" },
  { id: "overdue", label: "Overdue" },
];

export function TasksToolbar({
  view,
  onViewChange,
  mode,
  onModeChange,
  counts,
  onNewTask,
  onGenerateFromTemplate,
}: {
  view: SavedViewId;
  onViewChange: (next: SavedViewId) => void;
  mode: ViewMode;
  onModeChange: (next: ViewMode) => void;
  counts: SavedViewCounts;
  onNewTask: () => void;
  onGenerateFromTemplate?: () => void;
}) {
  return (
    <div className="toolbar">
      <div className="views">
        {VIEW_DEFS.map((v) => (
          <button
            type="button"
            key={v.id}
            className={`view-chip ${view === v.id ? "is-active" : ""}`}
            onClick={() => onViewChange(v.id)}
          >
            {v.label}
            <span className="mono">{counts[v.id]}</span>
          </button>
        ))}
      </div>
      <div className="ml-auto flex items-center gap-2">
        {onGenerateFromTemplate && (
          <Button variant="outline" size="sm" onClick={onGenerateFromTemplate}>
            <ClipboardList className="h-3.5 w-3.5 mr-1" />
            Add Template Tasks
          </Button>
        )}
        <div className="view-toggle">
          <button
            type="button"
            className={`vt-btn ${mode === "table" ? "is-active" : ""}`}
            onClick={() => onModeChange("table")}
          >
            <Rows3 className="h-3 w-3" /> Table
          </button>
          <button
            type="button"
            className={`vt-btn ${mode === "board" ? "is-active" : ""}`}
            onClick={() => onModeChange("board")}
          >
            <LayoutGrid className="h-3 w-3" /> Board
          </button>
        </div>
        <Button size="sm" onClick={onNewTask}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          New task
        </Button>
      </div>
    </div>
  );
}
